import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, todayISO } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Fish, Camera, Locate, X, Upload } from "lucide-react";
import exifr from "exifr";

export default function JournalDialog({ open, onOpenChange, assets, reservations, onSaved, defaultType = "captains_log", editing = null, defaultReservationId = "", defaultAssetId = "" }) {
  const t = useT();
  const { user } = useAuth();
  const canOverrideSkipper = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);
  const isEdit = !!editing;
  const [form, setForm] = useState(() => editing ? {
    log_type: editing.log_type || defaultType,
    asset_id: editing.asset_id || defaultAssetId || assets[0]?.id || "",
    reservation_id: editing.reservation_id || defaultReservationId || "",
    log_date: editing.log_date || todayISO(),
    route: editing.route || "", place_name: editing.place_name || "",
    weather: editing.weather || "", sea_state: editing.sea_state || "",
    fuel_liters: editing.fuel_liters ?? "", engine_hours: editing.engine_hours ?? "",
    incidents: editing.incidents || "", dos_note: editing.dos_note || "", donts_note: editing.donts_note || "",
    restaurant_review: editing.restaurant_review || "", bar_review: editing.bar_review || "",
    guest_experience_note: editing.guest_experience_note || "",
    return_checklist_ok: editing.return_checklist_ok || false,
    fishing_outcome: editing.fishing_outcome || "good", caught_species: editing.caught_species || "",
    kept_count: editing.kept_count ?? "", released_count: editing.released_count ?? "",
    bait_used: editing.bait_used || "", method_used: editing.method_used || "",
    success_rating: editing.success_rating ?? 3,
  } : {
    log_type: defaultType,
    asset_id: defaultAssetId || assets[0]?.id || "",
    reservation_id: defaultReservationId,
    log_date: todayISO(),
    route: "", place_name: "", weather: "", sea_state: "",
    fuel_liters: "", engine_hours: "",
    incidents: "", dos_note: "", donts_note: "",
    restaurant_review: "", bar_review: "", guest_experience_note: "",
    return_checklist_ok: false,
    fishing_outcome: "good", caught_species: "", kept_count: "", released_count: "",
    bait_used: "", method_used: "", success_rating: 3,
  });
  // Multi-day dock_overnight: `to_date` is only used when log_type === "dock_overnight"
  // and the user explicitly enters a range. When `to_date > log_date` we fan out
  // one POST per day on the server-side (the backend stays simple — one log per day).
  const [toDate, setToDate] = useState(editing?.log_date || "");
  // Skipper override — when the boat is under another owner's reservation but
  // a different person is actually on board (e.g. dock_overnight during a
  // maintenance block), an admin / owner_director can attribute the log to
  // someone else by picking a user here. Empty string = self (default).
  const [skipperOverride, setSkipperOverride] = useState(editing?.skipper_user_id || "");
  const [owners, setOwners] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load the user roster once the dialog opens so admins/owners can attribute
  // a log to a different skipper. Cheap call, only fired when needed.
  useEffect(() => {
    if (!open || !canOverrideSkipper || owners.length) return;
    api.get("/users")
      .then(({ data }) => {
        const rows = (data || []).filter(u => u.approval_status !== "pending");
        // Dedupe by display name — the roster can contain duplicate rows for the
        // same person, which would otherwise show twice in the override picker.
        const seen = new Set();
        const unique = rows.filter(u => {
          const key = (u.name || "").trim().toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setOwners(unique);
      })
      .catch(() => {});
  }, [open, canOverrideSkipper, owners.length]);
  // photos: [{file, preview, latitude, longitude, captured_at, status, caption}]
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    const prepared = [];
    for (const file of list) {
      if (!file.type.startsWith("image/")) continue;
      const preview = URL.createObjectURL(file);
      let latitude = null, longitude = null, captured_at = null;
      try {
        const gps = await exifr.gps(file);
        if (gps && gps.latitude != null) { latitude = gps.latitude; longitude = gps.longitude; }
        const parsed = await exifr.parse(file, ["DateTimeOriginal", "CreateDate"]).catch(() => null);
        if (parsed?.DateTimeOriginal) captured_at = new Date(parsed.DateTimeOriginal).toISOString();
        else if (parsed?.CreateDate) captured_at = new Date(parsed.CreateDate).toISOString();
      } catch {}
      prepared.push({ file, preview, latitude, longitude, captured_at, caption: "", status: latitude != null ? "geo-exif" : "geo-missing" });
    }
    setPhotos(p => [...p, ...prepared]);
  };

  const updatePhoto = (idx, patch) => setPhotos(p => p.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx));

  const stampCurrentLocation = (idx) => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => updatePhoto(idx, { latitude: pos.coords.latitude, longitude: pos.coords.longitude, status: "geo-device" }),
      (err) => toast.error(err.message || "Location denied"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const stampAllFromDevice = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPhotos(p => p.map(x => x.latitude == null ? { ...x, latitude: pos.coords.latitude, longitude: pos.coords.longitude, status: "geo-device" } : x));
        toast.success("Tagged all photos with current location");
      },
      (err) => toast.error(err.message || "Location denied"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const submit = async () => {
    if (!form.asset_id) { toast.error("Select an asset"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      body.fuel_liters = body.fuel_liters === "" ? null : parseFloat(body.fuel_liters);
      body.engine_hours = body.engine_hours === "" ? null : parseFloat(body.engine_hours);
      body.kept_count = body.kept_count === "" ? null : parseInt(body.kept_count);
      body.released_count = body.released_count === "" ? null : parseInt(body.released_count);
      body.success_rating = body.log_type === "fishing_log" ? parseInt(body.success_rating) : null;
      if (!body.reservation_id) delete body.reservation_id;
      // Multi-day dock stay: only send to_date for dock_overnight with a valid forward range.
      if (form.log_type === "dock_overnight" && toDate && toDate > form.log_date) {
        body.to_date = toDate;
      }
      // Skipper override (admins/owners only): attribute the log to another person.
      if (canOverrideSkipper && skipperOverride) {
        body.skipper_user_id = skipperOverride;
      }
      // Derive log geolocation from the first geotagged photo if the log doesn't already have one.
      // This feeds the fishing heatmap automatically when skippers shoot a catch photo.
      if (body.latitude == null || body.longitude == null) {
        const geoPhoto = photos.find(p => p.latitude != null && p.longitude != null);
        if (geoPhoto) {
          body.latitude = geoPhoto.latitude;
          body.longitude = geoPhoto.longitude;
        }
      }
      let log;
      if (isEdit) {
        const { data } = await api.patch(`/daily-logs/${editing.id}`, body);
        log = data;
      } else {
        const { data } = await api.post("/daily-logs", body);
        log = data;
      }
      // upload photos sequentially (keeps order and avoids bandwidth spikes)
      for (const p of photos) {
        const fd = new FormData();
        fd.append("file", p.file);
        fd.append("linked_type", "daily_log");
        fd.append("linked_id", log.id);
        if (p.caption) fd.append("caption", p.caption);
        if (p.latitude != null) fd.append("latitude", String(p.latitude));
        if (p.longitude != null) fd.append("longitude", String(p.longitude));
        if (p.captured_at) fd.append("captured_at", p.captured_at);
        try {
          await api.post("/media/upload", fd);
        } catch (e) {
          toast.error(`Photo "${p.file.name}" failed to upload`);
        }
      }
      if (!isEdit && log.fanned_out_count > 1) {
        toast.success(t("logDialog.multiDaySaved", { count: log.fanned_out_count }));
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{isEdit ? t("common.edit") + " · " + t("nav.journal") : t("logDialog.title")}</DialogTitle>
        </DialogHeader>

        <div>
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("logDialog.logType")}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
            {[
              { k: "captains_log", labelKey: "journal.filter.captain" },
              { k: "fishing_log", labelKey: "journal.filter.fishing" },
              { k: "excursion_log", labelKey: "journal.filter.excursion" },
              { k: "charter_log", labelKey: "journal.filter.charter" },
              { k: "dock_overnight", labelKey: "journal.filter.dockOvernight" },
            ].map(({k, labelKey}) => (
              <button key={k} type="button" onClick={() => update("log_type", k)}
                data-testid={`log-type-${k}`}
                className={`px-3 py-2 rounded-md border text-xs ${form.log_type === k ? "bg-[#007AFF]/15 border-[#007AFF]/50" : "bg-[#050A14] border-[#1E293B]"}`}>{t(labelKey)}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label={t("logDialog.asset")}>
            <select value={form.asset_id} onChange={(e) => update("asset_id", e.target.value)}
              data-testid="log-asset-select"
              className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label={form.log_type === "dock_overnight" ? t("logDialog.fromDate") : t("logDialog.date")}>
            <Input type="date" value={form.log_date} onChange={(e) => update("log_date", e.target.value)} data-testid="log-date-input" className="bg-[#050A14] border-[#1E293B]" />
          </Field>
        </div>

        {form.log_type === "dock_overnight" && !isEdit && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label={t("logDialog.toDate")}>
              <Input type="date" value={toDate} min={form.log_date}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="log-to-date-input" className="bg-[#050A14] border-[#1E293B]" />
            </Field>
            <div className="flex items-end">
              <p className="text-[10px] text-[#94A3B8] leading-snug pb-2">{t("logDialog.toDateHint")}</p>
            </div>
          </div>
        )}

        {canOverrideSkipper && !isEdit && (
          <div className="mt-3">
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("logDialog.skipperOverride")}</Label>
            <select value={skipperOverride} onChange={(e) => setSkipperOverride(e.target.value)}
              className="w-full mt-1 bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm"
              data-testid="log-skipper-override-select">
              <option value="">{t("logDialog.skipperSelf")}</option>
              {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <p className="text-[10px] text-[#94A3B8] mt-1 leading-snug">{t("logDialog.skipperOverrideHint")}</p>
          </div>
        )}

        {reservations.length > 0 && (
          <div className="mt-3">
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("logDialog.linkedReservation")}</Label>
            <select value={form.reservation_id} onChange={(e) => update("reservation_id", e.target.value)}
              className="w-full mt-1 bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm"
              data-testid="log-reservation-select">
              <option value="">{t("common.none")}</option>
              {reservations.filter(r => !form.asset_id || r.asset_id === form.asset_id).map(r => {
                const label = `${t(`reservation.${r.reservation_type}`)} · ${r.start_date} → ${r.end_date}`;
                return <option key={r.id} value={r.id}>{label}</option>;
              })}
            </select>
          </div>
        )}

        <Tabs defaultValue="basics" className="mt-4">
          <TabsList className="bg-[#050A14] border border-[#1E293B] w-full overflow-x-auto flex-nowrap justify-start whitespace-nowrap">
            <TabsTrigger value="basics" data-testid="tab-basics">{t("logDialog.tabs.basics")}</TabsTrigger>
            <TabsTrigger value="conditions" data-testid="tab-conditions">{t("logDialog.tabs.conditions")}</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">{t("logDialog.tabs.notes")}</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos"><Camera className="w-3 h-3 mr-1" /> {t("logDialog.tabs.photos")}{photos.length ? ` (${photos.length})` : ""}</TabsTrigger>
            {form.log_type === "fishing_log" && <TabsTrigger value="fishing" data-testid="tab-fishing"><Fish className="w-3 h-3 mr-1" /> {t("logDialog.tabs.fishing")}</TabsTrigger>}
          </TabsList>
          <TabsContent value="basics" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("logDialog.place")}><Input value={form.place_name} onChange={(e) => update("place_name", e.target.value)} placeholder={t("logDialog.placePlaceholder")} data-testid="log-place-input" className="bg-[#050A14] border-[#1E293B]" /></Field>
              <Field label={t("logDialog.route")}><Input value={form.route} onChange={(e) => update("route", e.target.value)} placeholder={t("logDialog.routePlaceholder")} className="bg-[#050A14] border-[#1E293B]" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("logDialog.fuel")}><Input type="number" value={form.fuel_liters} onChange={(e) => update("fuel_liters", e.target.value)} data-testid="log-fuel-input" className="bg-[#050A14] border-[#1E293B]" /></Field>
              <Field label={t("logDialog.engineHours")}><Input type="number" value={form.engine_hours} onChange={(e) => update("engine_hours", e.target.value)} data-testid="log-engine-input" className="bg-[#050A14] border-[#1E293B]" /></Field>
            </div>
          </TabsContent>
          <TabsContent value="conditions" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("logDialog.weather")}><Input value={form.weather} onChange={(e) => update("weather", e.target.value)} placeholder={t("logDialog.weatherPlaceholder")} className="bg-[#050A14] border-[#1E293B]" /></Field>
              <Field label={t("logDialog.seaState")}><Input value={form.sea_state} onChange={(e) => update("sea_state", e.target.value)} placeholder={t("logDialog.seaPlaceholder")} className="bg-[#050A14] border-[#1E293B]" /></Field>
            </div>
            <Field label={t("logDialog.incidents")}><Textarea value={form.incidents} onChange={(e) => update("incidents", e.target.value)} placeholder={t("logDialog.incidentsPlaceholder")} data-testid="log-incidents-input" className="bg-[#050A14] border-[#1E293B]" /></Field>
          </TabsContent>
          <TabsContent value="notes" className="space-y-3 mt-4">
            <Field label={t("logDialog.whatDo")}><Textarea value={form.dos_note} onChange={(e) => update("dos_note", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
            <Field label={t("logDialog.whatDont")}><Textarea value={form.donts_note} onChange={(e) => update("donts_note", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
            <Field label={t("logDialog.restaurantReview")}><Textarea value={form.restaurant_review} onChange={(e) => update("restaurant_review", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
            <Field label={t("logDialog.barReview")}><Textarea value={form.bar_review} onChange={(e) => update("bar_review", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
            <Field label={t("logDialog.guestExperience")}><Textarea value={form.guest_experience_note} onChange={(e) => update("guest_experience_note", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
          </TabsContent>
          <TabsContent value="photos" className="space-y-3 mt-4" data-testid="photos-tab-content">
            <div
              onClick={() => fileRef.current?.click()}
              className="border border-dashed border-[#1E293B] rounded-md p-6 text-center cursor-pointer hover:border-[#4DA6FF] transition-colors"
              data-testid="log-photo-dropzone"
            >
              <Upload className="w-6 h-6 mx-auto mb-2 text-[#94A3B8]" />
              <div className="text-sm">{t("logDialog.photosDrop")}</div>
              <div className="text-[11px] text-[#94A3B8] mt-1">{t("logDialog.photosHint")}</div>
              <input
                ref={fileRef} type="file" multiple accept="image/*,.heic,.heif"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                data-testid="log-photo-input"
              />
            </div>

            {photos.length > 0 && (
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("logDialog.photosCount", { count: photos.length })}</div>
                <Button type="button" onClick={stampAllFromDevice} data-testid="log-stamp-all-geo"
                  className="h-7 text-xs bg-[#0B1221] border border-[#1E293B] hover:bg-[#131C31]">
                  <Locate className="w-3 h-3 mr-1" /> {t("logDialog.tagAllMissing")}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {photos.map((p, idx) => (
                <div key={idx} className="bg-[#050A14] border border-[#1E293B] rounded-md p-3" data-testid={`log-photo-row-${idx}`}>
                  <div className="flex gap-3">
                    <img src={p.preview} alt="" className="w-20 h-20 object-cover rounded-md border border-[#1E293B] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-medium truncate">{p.file.name}</div>
                        <button type="button" onClick={() => removePhoto(idx)} className="text-[#F87171] hover:text-[#EF4444]"
                          data-testid={`log-photo-remove-${idx}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="mt-1">
                        {p.latitude != null ? (
                          <div className="font-mono text-[10px] text-[#34D399] flex items-center gap-1">
                            <Locate className="w-3 h-3" /> {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}
                            <span className="text-[#94A3B8]">· {p.status === "geo-exif" ? t("logDialog.geoFromExif") : p.status === "geo-device" ? t("logDialog.geoDevice") : t("logDialog.geoManual")}</span>
                          </div>
                        ) : (
                          <div className="font-mono text-[10px] text-[#FBBF24] flex items-center gap-1">
                            <Locate className="w-3 h-3" /> {t("logDialog.noLocation")}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <button type="button" onClick={() => stampCurrentLocation(idx)}
                          data-testid={`log-photo-geo-${idx}`}
                          className="text-[10px] font-mono bg-[#0B1221] border border-[#1E293B] hover:border-[#4DA6FF] rounded px-2 py-1">
                          {t("logDialog.useMyLocation")}
                        </button>
                        <input
                          type="text" placeholder="lat,lng" value={p.latitude != null ? `${p.latitude},${p.longitude}` : ""}
                          onChange={(e) => {
                            const [la, ln] = e.target.value.split(",").map(s => parseFloat(s.trim()));
                            if (!isNaN(la) && !isNaN(ln)) updatePhoto(idx, { latitude: la, longitude: ln, status: "manual" });
                            else if (e.target.value === "") updatePhoto(idx, { latitude: null, longitude: null });
                          }}
                          className="text-[10px] font-mono bg-[#0B1221] border border-[#1E293B] rounded px-2 py-1 flex-1 min-w-[90px]"
                          data-testid={`log-photo-coords-${idx}`}
                        />
                      </div>
                      <Input
                        value={p.caption} onChange={(e) => updatePhoto(idx, { caption: e.target.value })}
                        placeholder={t("logDialog.caption")}
                        data-testid={`log-photo-caption-${idx}`}
                        className="mt-2 bg-[#0B1221] border-[#1E293B] h-7 text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          {form.log_type === "fishing_log" && (
            <TabsContent value="fishing" className="space-y-3 mt-4">
              <div>
                <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("logDialog.outcome")}</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {["excellent", "good", "poor", "skunked"].map(o => (
                    <button key={o} type="button" onClick={() => update("fishing_outcome", o)}
                      data-testid={`fishing-outcome-${o}`}
                      className={`px-3 py-2 rounded-md border text-xs capitalize ${form.fishing_outcome === o ? "bg-[#8B5CF6]/15 border-[#8B5CF6]/50" : "bg-[#050A14] border-[#1E293B]"}`}>{t(`logDialog.outcome.${o}`)}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("logDialog.species")}><Input value={form.caught_species} onChange={(e) => update("caught_species", e.target.value)} placeholder={t("logDialog.speciesPlaceholder")} data-testid="fishing-species-input" className="bg-[#050A14] border-[#1E293B]" /></Field>
                <Field label={t("logDialog.method")}><Input value={form.method_used} onChange={(e) => update("method_used", e.target.value)} placeholder={t("logDialog.methodPlaceholder")} className="bg-[#050A14] border-[#1E293B]" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label={t("logDialog.bait")}><Input value={form.bait_used} onChange={(e) => update("bait_used", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
                <Field label={t("logDialog.kept")}><Input type="number" value={form.kept_count} onChange={(e) => update("kept_count", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
                <Field label={t("logDialog.released")}><Input type="number" value={form.released_count} onChange={(e) => update("released_count", e.target.value)} className="bg-[#050A14] border-[#1E293B]" /></Field>
              </div>
              <div>
                <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("logDialog.successRating")}</Label>
                <div className="flex gap-1 mt-2">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => update("success_rating", n)}
                      data-testid={`fishing-star-${n}`}
                      className={`w-9 h-9 rounded-md border text-lg ${n <= form.success_rating ? "bg-[#8B5CF6]/15 border-[#8B5CF6]/50 text-[#A78BFA]" : "bg-[#050A14] border-[#1E293B] text-[#94A3B8]"}`}>★</button>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="log-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            {saving ? t("common.saving") : t("logDialog.saveEntry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{label.toUpperCase()}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
